import { API_BASE } from '../../config/apiBase';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '../../components/ui/dialog';
import {
  Plus, TrendingUp, Users, DollarSign, CheckCircle, Clock,
  ExternalLink, Copy, ArrowRight, Eye, Phone, Mail, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const API = API_BASE;

const STATUS_COLORS = {
  deal_created: 'bg-gray-500',
  loi_generated: 'bg-blue-500',
  loi_sent: 'bg-indigo-500',
  loi_viewed: 'bg-purple-500',
  activation_pending: 'bg-amber-500',
  activated: 'bg-emerald-500',
  conditional_approval_issued: 'bg-teal-500',
  documents_pending: 'bg-orange-500',
  final_review: 'bg-cyan-500',
  approved_for_funding: 'bg-green-500',
  funded: 'bg-emerald-600',
};

const DealsPipeline = () => {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', company_name: '',
    equipment_description: '', equipment_cost: '', equipment_year: '', equipment_make: '', equipment_model: '',
    down_payment_pct: '10', origination_fee: '', term_months: '48', rate: '', monthly_payment: '',
    program_type: 'EFA', funding_manager: '', funding_manager_phone: '', notes: ''
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [dealsRes, statsRes] = await Promise.all([
        axios.get(`${API}/deals/list`, { headers }),
        axios.get(`${API}/deals/pipeline-stats`, { headers })
      ]);
      setDeals(dealsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!form.first_name || !form.last_name || !form.email || !form.equipment_description || !form.equipment_cost) {
      toast.error('Complete required fields');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/deals/create`, {
        customer: {
          first_name: form.first_name, last_name: form.last_name,
          email: form.email, phone: form.phone, company_name: form.company_name
        },
        equipment: {
          description: form.equipment_description, cost: parseFloat(form.equipment_cost),
          year: form.equipment_year ? parseInt(form.equipment_year) : null,
          make: form.equipment_make || null, model: form.equipment_model || null
        },
        terms: {
          equipment_cost: parseFloat(form.equipment_cost),
          down_payment_pct: parseFloat(form.down_payment_pct || 10),
          origination_fee: parseFloat(form.origination_fee || 0),
          term_months: parseInt(form.term_months || 48),
          rate: form.rate ? parseFloat(form.rate) : null,
          monthly_payment: form.monthly_payment ? parseFloat(form.monthly_payment) : null,
          program_type: form.program_type
        },
        funding_manager: form.funding_manager,
        funding_manager_phone: form.funding_manager_phone,
        notes: form.notes
      }, { headers });
      toast.success('Deal created!');
      setShowCreate(false);
      setForm({
        first_name: '', last_name: '', email: '', phone: '', company_name: '',
        equipment_description: '', equipment_cost: '', equipment_year: '', equipment_make: '', equipment_model: '',
        down_payment_pct: '10', origination_fee: '', term_months: '48', rate: '', monthly_payment: '',
        program_type: 'EFA', funding_manager: '', funding_manager_phone: '', notes: ''
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error creating deal');
    } finally {
      setCreating(false);
    }
  };

  const handleGenerateLOI = async (dealId) => {
    try {
      await axios.post(`${API}/deals/generate-loi/${dealId}`, {}, { headers });
      toast.success('LOI Generated');
      fetchData();
    } catch (err) {
      toast.error('Error generating LOI');
    }
  };

  const handleSendLOI = async (dealId) => {
    try {
      const res = await axios.post(`${API}/deals/send-loi/${dealId}`, {}, { headers });
      const url = `${window.location.origin}/activate/${res.data.activation_token}`;
      await navigator.clipboard.writeText(url);
      toast.success('LOI sent! Activation link copied to clipboard.');
      fetchData();
    } catch (err) {
      toast.error('Error sending LOI');
    }
  };

  const copyActivationLink = (deal) => {
    const url = `${window.location.origin}/activate/${deal.activation_token}`;
    navigator.clipboard.writeText(url);
    toast.success('Activation link copied!');
  };

  const copyPortalLink = (deal) => {
    const url = `${window.location.origin}/portal/${deal.activation_token}`;
    navigator.clipboard.writeText(url);
    toast.success('Portal link copied!');
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="deals-pipeline">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">Deal Pipeline</h1>
          <p className="text-muted-foreground">Manage deals from creation to funding</p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button className="bg-red-600 hover:bg-red-700" data-testid="new-deal-btn">
              <Plus className="w-4 h-4 mr-2" /> New Deal
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Deal</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="col-span-2"><p className="font-semibold text-sm text-muted-foreground">Customer</p></div>
              <div><Label>First Name *</Label><Input value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} /></div>
              <div><Label>Last Name *</Label><Input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} /></div>
              <div><Label>Email *</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
              <div className="col-span-2"><Label>Company</Label><Input value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} /></div>
              
              <div className="col-span-2 pt-2"><p className="font-semibold text-sm text-muted-foreground">Equipment</p></div>
              <div className="col-span-2"><Label>Description *</Label><Input value={form.equipment_description} onChange={e => setForm({...form, equipment_description: e.target.value})} placeholder="2024 Freightliner Cascadia" /></div>
              <div><Label>Cost *</Label><Input type="number" value={form.equipment_cost} onChange={e => setForm({...form, equipment_cost: e.target.value})} /></div>
              <div><Label>Year</Label><Input type="number" value={form.equipment_year} onChange={e => setForm({...form, equipment_year: e.target.value})} /></div>
              
              <div className="col-span-2 pt-2"><p className="font-semibold text-sm text-muted-foreground">Terms</p></div>
              <div><Label>Down Payment %</Label><Input type="number" value={form.down_payment_pct} onChange={e => setForm({...form, down_payment_pct: e.target.value})} /></div>
              <div><Label>Origination Fee ($)</Label><Input type="number" value={form.origination_fee} onChange={e => setForm({...form, origination_fee: e.target.value})} /></div>
              <div><Label>Term (months)</Label><Input type="number" value={form.term_months} onChange={e => setForm({...form, term_months: e.target.value})} /></div>
              <div><Label>Program</Label>
                <select className="w-full h-10 border rounded-md px-3 text-sm bg-background" value={form.program_type} onChange={e => setForm({...form, program_type: e.target.value})}>
                  <option value="EFA">EFA</option><option value="ELA">ELA</option><option value="SBA">SBA</option>
                </select>
              </div>
              <div><Label>Rate (%)</Label><Input type="number" step="0.01" value={form.rate} onChange={e => setForm({...form, rate: e.target.value})} /></div>
              <div><Label>Monthly Payment ($)</Label><Input type="number" value={form.monthly_payment} onChange={e => setForm({...form, monthly_payment: e.target.value})} /></div>

              <div className="col-span-2 pt-2"><p className="font-semibold text-sm text-muted-foreground">Funding Manager</p></div>
              <div><Label>Name</Label><Input value={form.funding_manager} onChange={e => setForm({...form, funding_manager: e.target.value})} /></div>
              <div><Label>Phone</Label><Input value={form.funding_manager_phone} onChange={e => setForm({...form, funding_manager_phone: e.target.value})} /></div>
              <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={creating} className="bg-red-600 hover:bg-red-700">
                {creating ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                Create Deal
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Pipeline Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Deals', value: stats.total_deals, icon: Users, color: 'text-blue-600' },
            { label: 'Activated', value: stats.total_activated, icon: CheckCircle, color: 'text-emerald-600' },
            { label: 'Funded', value: stats.total_funded, icon: DollarSign, color: 'text-green-600' },
            { label: 'Activation Rate', value: `${stats.activation_rate}%`, icon: TrendingUp, color: 'text-red-600' },
          ].map(({ label, value, icon: Icon, color }) => (
            <Card key={label}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={cn("p-2 rounded-lg bg-muted", color)}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="text-xl font-bold">{value}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pipeline Bar */}
      {stats?.pipeline && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-1 overflow-x-auto pb-2">
              {stats.pipeline.map((stage) => (
                <div key={stage.status} className="flex items-center gap-1">
                  <div className={cn(
                    "flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-white whitespace-nowrap",
                    STATUS_COLORS[stage.status] || 'bg-gray-500'
                  )}>
                    {stage.label?.en?.split(' ').slice(0, 2).join(' ')} ({stage.count})
                  </div>
                  <ArrowRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Deals List */}
      <div className="space-y-3">
        {deals.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold">No deals yet</h3>
              <p className="text-muted-foreground mb-4">Create your first deal to start the pipeline</p>
              <Button onClick={() => setShowCreate(true)} className="bg-red-600 hover:bg-red-700">
                <Plus className="w-4 h-4 mr-2" /> Create First Deal
              </Button>
            </CardContent>
          </Card>
        ) : deals.map((deal) => (
          <Card key={deal.id} className="hover:border-red-200 transition-colors" data-testid={`deal-card-${deal.id}`}>
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                {/* Deal info */}
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">
                      {deal.customer?.first_name} {deal.customer?.last_name}
                    </h3>
                    <Badge className={cn("text-white text-xs", STATUS_COLORS[deal.status] || 'bg-gray-500')}>
                      {deal.status?.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{deal.equipment?.description}</p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />${(deal.equipment?.cost || 0).toLocaleString()}</span>
                    <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{deal.customer?.email}</span>
                    {deal.customer?.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{deal.customer.phone}</span>}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  {deal.status === 'deal_created' && (
                    <Button size="sm" variant="outline" onClick={() => handleGenerateLOI(deal.id)} data-testid={`generate-loi-${deal.id}`}>
                      Generate LOI
                    </Button>
                  )}
                  {deal.status === 'loi_generated' && (
                    <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => handleSendLOI(deal.id)} data-testid={`send-loi-${deal.id}`}>
                      Send LOI
                    </Button>
                  )}
                  {['loi_sent', 'loi_viewed', 'activation_pending'].includes(deal.status) && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => copyActivationLink(deal)} data-testid={`copy-activation-${deal.id}`}>
                        <Copy className="w-3 h-3 mr-1" /> Activation Link
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => window.open(`/activate/${deal.activation_token}`, '_blank')}>
                        <ExternalLink className="w-3 h-3 mr-1" /> Preview
                      </Button>
                    </>
                  )}
                  {['activated', 'conditional_approval_issued', 'documents_pending', 'final_review', 'approved_for_funding', 'funded'].includes(deal.status) && (
                    <Button size="sm" variant="outline" onClick={() => copyPortalLink(deal)} data-testid={`copy-portal-${deal.id}`}>
                      <Copy className="w-3 h-3 mr-1" /> Portal Link
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => navigate(`/deals/${deal.id}`)} data-testid={`view-deal-${deal.id}`}>
                    <Eye className="w-3 h-3 mr-1" /> Details
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default DealsPipeline;
